import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  AbstractComponent,
  ActivityConclusionPayload,
  Card,
  CardService,
  NextStepPreview,
  Stage,
  StageKindEnum,
  StageService,
  TASK_OUTCOME_LABEL,
  Task,
  TaskOutcomeEnum,
  TasksService,
  TaskTypeEnum,
  taskTypeLabelKey,
} from 'lib-trend-core';
import { forkJoin, of, takeUntil } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SelectLossReasonComponent, SelectLossReasonResult } from '../../../kanban/loss-reason/select-loss-reason/select-loss-reason.component';
import { PresentationDateDialogComponent, PresentationDateDialogResult } from '../../../kanban/presentation-date-dialog/presentation-date-dialog.component';
import { NextActivityFormComponent } from '../next-activity-form/next-activity-form.component';
import { ScheduleMoment } from '../schedule-moment-field/schedule-moment-field.component';

/**
 * What the dialog is opened with: the activity, or just its id when the caller only holds the
 * deal's `nextAction` pointer (the board card). The deal is always re-read: the caller's copy may
 * be a `{ _id, name }` join, and the dialog needs its stage, funnel, value and presentation date.
 */
export interface ConcludeActivityDialogData {
  task?: Task;
  idTask?: string;
  card?: Card | { _id: string; name?: string };
}

/** What the dialog closes with after a successful conclusion; nothing when abandoned (RF-13). */
export interface ConcludeActivityDialogResult {
  task: Task;
  nextTask?: Task;
}

type NextStepMode = 'schedule' | 'skip' | 'none';

/**
 * The conclusion of a deal activity, in two steps and ONE submit
 * (SPEC-2026-10-crm-activities-cadence — RF-07 to RF-17, RF-20 to RF-23 · plan AD-02):
 *
 *  1. the outcome, its moment (never ahead of now), duration and notes, and — collapsed — a move
 *     of the deal and its value, reusing the board's own loss-reason and presentation-date dialogs;
 *  2. the next step, required when the deal would stay open with nothing scheduled and offered
 *     otherwise, pre-filled from the API's suggestion with every field editable — or skipped with
 *     a written reason.
 *
 * "Next" only asks the API for the preview; nothing is written until "Concluir" submits both steps
 * to the conclusion command, so abandoning the dialog leaves the activity open and unchanged
 * (AC-13). A failed preview leaves step 2 empty but usable (RNF-07); the API's refusals are shown
 * as they come, already translated.
 */
@Component({
  selector: 'app-conclude-activity-dialog',
  templateUrl: './conclude-activity-dialog.component.html',
  styleUrls: ['./conclude-activity-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ConcludeActivityDialogComponent extends AbstractComponent implements OnInit {

  step: 1 | 2 = 1;
  override loading = true;
  previewing = false;
  previewFailed = false;
  saving = false;

  task?: Task;
  card?: Card;
  stages: Array<Stage> = [];

  readonly outcomes = Object.values(TaskOutcomeEnum).map((value) => ({ value, label: TASK_OUTCOME_LABEL[value] }));
  readonly maxDate = new Date();

  outcomeForm!: FormGroup;
  nextForm!: FormGroup;
  readonly skipReason = new FormControl<string>('', { nonNullable: true, validators: [Validators.required] });

  moveOpen = false;
  lossReason?: SelectLossReasonResult;
  presentedAt?: string;

  preview?: NextStepPreview;
  mode: NextStepMode = 'none';

  constructor(
    injector: Injector,
    private readonly dialog: MatDialog,
    private readonly dialogRef: MatDialogRef<ConcludeActivityDialogComponent, ConcludeActivityDialogResult | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: ConcludeActivityDialogData,
    private readonly cardService: CardService,
    private readonly tasksService: TasksService,
    private readonly stageService: StageService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.outcomeForm = this.formBuilder.group({
      outcome: [TaskOutcomeEnum.HELD, Validators.required],
      moment: [<ScheduleMoment>{ dueAt: new Date(), untimed: false }, Validators.required],
      durationMinutes: [null, [Validators.min(0)]],
      note: [''],
      idStage: [''],
      budget: [null],
    });
    this.nextForm = NextActivityFormComponent.buildForm(this.formBuilder);
    this.load();
  }

  // ---- loading -------------------------------------------------------------------------------

  private load(): void {
    const idTask = this.idTask;
    const idCard = this.idOf(this.data?.card) || this.idOf(this.data?.task?.card);

    if (!idTask || !idCard) {
      this.alertService.error(this.t('task.conclude.loadError'));
      this.dialogRef.close(undefined);
      return;
    }

    const needsTask = !this.data?.task?.type;

    forkJoin({
      card: this.cardService.getById(idCard),
      task: needsTask ? this.tasksService.getById(idTask) : of(this.data.task as Task),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ card, task }) => {
          this.card = card;
          this.task = task;
          this.outcomeForm.patchValue({ budget: card?.budget ?? null }, { emitEvent: false });
          this.loading = false;
          this.loadStages(this.idOf(card?.funnel));
        },
        error: (error) => {
          this.alertService.error(error?.error?.message ?? this.t('task.conclude.loadError'));
          this.dialogRef.close(undefined);
        },
      });
  }

  private loadStages(idFunnel: string): void {
    if (!idFunnel) return;
    this.stageService.getStagesByFunnel(idFunnel)
      .pipe(takeUntil(this.destroy$), catchError(() => of([] as Array<Stage>)))
      .subscribe((stages) => (this.stages = (stages ?? []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))));
  }

  // ---- step 1 --------------------------------------------------------------------------------

  get idTask(): string {
    return this.idOf(this.data?.task) || `${this.data?.idTask ?? ''}`;
  }

  get taskTitle(): string {
    const name = this.task?.name?.trim();
    const type = this.task?.type;
    return name && name !== type ? name : this.t(taskTypeLabelKey(type));
  }

  get currentStageId(): string {
    return this.idOf(this.card?.stage);
  }

  get currentStageName(): string {
    return (this.card?.stage as Stage)?.name ?? '';
  }

  /** Every other stage of the deal's pipeline, in order. */
  get stageOptions(): Array<Stage> {
    return this.stages.filter((stage) => stage._id !== this.currentStageId);
  }

  get selectedStage(): Stage | undefined {
    const idStage = `${this.outcomeForm?.get('idStage')?.value ?? ''}`;
    return idStage ? this.stages.find((stage) => stage._id === idStage) : undefined;
  }

  /** The stage the deal is in after this conclusion: the chosen one, or the current one. */
  get stageAfter(): Stage | undefined {
    return this.selectedStage ?? (this.card?.stage as Stage | undefined);
  }

  /** Whether the deal stays open after the move — the API's answer when it came, else read from the stage. */
  get staysOpen(): boolean {
    if (this.preview) return this.preview.staysOpen;
    const stage = this.stageAfter;
    return !(stage?.wonDeal === true || stage?.lostDeal === true);
  }

  toggleMove(): void {
    this.moveOpen = !this.moveOpen;
  }

  /**
   * A move chosen in the dialog goes through the same gates the board applies before a drop:
   * a lost stage asks for the reason (platform discards included), a presentation stage offers the
   * date. Cancelling either keeps the deal where it is.
   */
  onStageChange(idStage: string): void {
    this.lossReason = undefined;
    this.presentedAt = undefined;
    this.preview = undefined;
    const stage = this.stages.find((candidate) => candidate._id === idStage);
    if (!stage) return;

    if (stage.lostDeal === true) {
      this.dialog.open(SelectLossReasonComponent, { width: '700px', disableClose: true, data: { card: this.card } })
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((result: SelectLossReasonResult | undefined) => {
          if (result?.idLossReason) {
            this.lossReason = result;
          } else {
            this.outcomeForm.patchValue({ idStage: '' });
          }
        });
      return;
    }

    if (stage.kind === StageKindEnum.PRESENTATION && !this.card?.presentedAt) {
      this.dialog.open(PresentationDateDialogComponent, { width: '560px', disableClose: true, data: { card: this.card } })
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((result: PresentationDateDialogResult | undefined) => {
          if (!result) {
            this.outcomeForm.patchValue({ idStage: '' });
            return;
          }
          if ('presentedAt' in result) this.presentedAt = result.presentedAt;
        });
    }
  }

  /** "Next": validates step 1 and asks the API what step 2 is — required, offered, or none (deal closing). */
  goNext(): void {
    if (this.outcomeForm.invalid || this.previewing) {
      this.outcomeForm.markAllAsTouched();
      return;
    }

    const idCard = this.idOf(this.card);
    const value = this.outcomeForm.value;
    this.previewing = true;
    this.previewFailed = false;

    this.cardService.nextStepPreview(idCard, this.idTask, { outcome: value.outcome, idStage: value.idStage || undefined })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (preview) => {
          this.preview = preview;
          this.previewing = false;
          this.mode = preview.required ? 'schedule' : 'none';
          this.nextForm = NextActivityFormComponent.buildForm(this.formBuilder, {
            type: preview.suggestion?.type ?? TaskTypeEnum.CALL,
            // The title arrives as a key and is translated here; what the user confirms is what is saved (RF-16).
            name: preview.suggestion?.titleKey ? this.t(preview.suggestion.titleKey) : '',
            moment: preview.suggestion?.dueAt ? { dueAt: new Date(preview.suggestion.dueAt), untimed: false } : null,
          });
          this.step = 2;
        },
        error: () => {
          // RNF-07: a failed suggestion never prevents scheduling by hand. Step 2 opens empty; the
          // API still applies the step-2 rule on submit.
          this.preview = undefined;
          this.previewFailed = true;
          this.previewing = false;
          this.mode = 'schedule';
          this.nextForm = NextActivityFormComponent.buildForm(this.formBuilder);
          this.step = 2;
        },
      });
  }

  // ---- step 2 --------------------------------------------------------------------------------

  get required(): boolean {
    return this.preview?.required === true;
  }

  setMode(mode: NextStepMode): void {
    this.mode = mode;
  }

  back(): void {
    this.step = 1;
  }

  /** One submit for both steps (AD-02). */
  finish(): void {
    if (this.saving) return;

    const willSchedule = this.staysOpen && this.mode === 'schedule';
    const willSkip = this.staysOpen && this.mode === 'skip';

    if (willSchedule && this.nextForm.invalid) {
      this.nextForm.markAllAsTouched();
      return;
    }
    if (willSkip && this.skipReason.invalid) {
      this.skipReason.markAsTouched();
      return;
    }

    const value = this.outcomeForm.value;
    const moment = value.moment as ScheduleMoment;
    const budget = value.budget;
    const budgetChanged = budget !== null && budget !== undefined && `${budget}` !== '' && Number(budget) !== Number(this.card?.budget ?? 0);

    const body: ActivityConclusionPayload = {
      outcome: value.outcome,
      occurredAt: new Date(moment.dueAt).toISOString(),
      durationMinutes: value.durationMinutes === null || value.durationMinutes === undefined || `${value.durationMinutes}` === '' ? undefined : Number(value.durationMinutes),
      note: `${value.note ?? ''}`.trim() || undefined,
      move: value.idStage
        ? { idStage: value.idStage, idLossReason: this.lossReason?.idLossReason, lossNote: this.lossReason?.lossNote, presentedAt: this.presentedAt }
        : undefined,
      budget: budgetChanged ? Number(budget) : undefined,
      next: willSchedule ? NextActivityFormComponent.toPayload(this.nextForm.value) : undefined,
      skipReason: willSkip ? this.skipReason.value.trim() : undefined,
    };

    this.saving = true;
    this.cardService.concludeActivity(this.idOf(this.card), this.idTask, body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          // Called on data arrival, not in ngOnInit: `t()` before the catalog loads returns the raw key.
          this.alertService.success(this.t('task.conclude.saved'));
          this.dialogRef.close(result);
        },
        error: (error) => {
          this.saving = false;
          // The API's refusals arrive translated by the exception filter (nextStepRequired, bantIncomplete…).
          this.alertService.error(error?.error?.message ?? this.t('task.conclude.error'));
        },
      });
  }

  close(): void {
    this.dialogRef.close(undefined);
  }

  private idOf(value: unknown): string {
    return `${(value as { _id?: unknown })?._id ?? value ?? ''}`;
  }
}
